/**
 * Logging configuration
 *
 * see https://www.npmjs.com/package/react-native-logs
 */
import type { SimpleTask } from "react-native"

import { InteractionManager, Platform } from "react-native"
import * as Logging from "react-native-logs"
import type { ConsoleTransportOptions } from "react-native-logs/dist/transports/consoleTransport"

import type { $Keys } from "utility-types"

const LEVELS = {
  debug: 0,
  error: 3,
  info: 1,
  warn: 2,
} as const
type LogLevel = keyof typeof LEVELS
const COLORS: NonNullable<ConsoleTransportOptions["colors"]> = {
  debug: "grey",
  error: "redBright",
  info: "blueBright",
  warn: "magenta",
}

let LOG_LEVEL: LogLevel = "error"
const OVERRIDE = process.env.EXPO_PUBLIC_LOG_LEVEL?.toLocaleLowerCase()
if (typeof OVERRIDE === "string") {
  if (typeof LEVELS[OVERRIDE as keyof typeof LEVELS] === "undefined") {
    // eslint-disable-next-line no-console
    console.warn(`Invalid log level: ${OVERRIDE}`)
  } else {
    LOG_LEVEL = OVERRIDE as LogLevel
  }
}

// This seems to just report every log as a sentry error?
//   transport = Logging.sentryTransport
//   transportOptions = { SENTRY: Sentry }

export const logger = Logging.logger.createLogger<
  Logging.transportFunctionType<ConsoleTransportOptions>,
  LogLevel
>({
  async: true,
  asyncFunc: (task: SimpleTask) =>
    InteractionManager.runAfterInteractions(task),
  levels: LEVELS,
  severity: LOG_LEVEL,
  transport: (props: {
    msg: string
    rawMsg: unknown
    level: { severity: number; text: string }
    extension?: string | null
    options?: ConsoleTransportOptions
  }) => {
    const prefix = Platform.OS
    props.msg = `[${prefix}] ${props.msg}`
    return Logging.consoleTransport(props)
  },
  transportOptions: {
    colors: COLORS,
  },
})
type LogFn = (...args: unknown[]) => void
type LoggerMethods = {
  [key in LogLevel]: LogFn
}

// TODO: Delete this eventually
export function makeFileLogger(filename: string | { fileName: string }) {
  let prefix: string
  if (typeof filename === "string") {
    prefix = filename
  } else {
    prefix = filename.fileName
  }
  const loggerMethods = {} as LoggerMethods
  for (const key of Object.keys(LEVELS) as Array<$Keys<typeof LEVELS>>) {
    loggerMethods[key] = (...args: unknown[]) => {
      return logger[key](`[${prefix}]: `, ...args)
    }
  }
  return loggerMethods
}

function disableWarningLogs() {
  if (process.env.EXPO_PUBLIC_DISABLE_LOG_WARNINGS === "true") {
    // eslint-disable-next-line no-console
    console.warn("console.warn is disabled")
    // eslint-disable-next-line no-console, @typescript-eslint/no-empty-function
    console.warn = () => {}
  }
}
disableWarningLogs()
